export type LinearGradientPoint = { x: number; y: number };

export type LinearGradientConfig = {
  colors: string[];
  start?: LinearGradientPoint;
  end?: LinearGradientPoint;
  locations?: number[];
};

const DEFAULT_GRADIENT: Required<
  Pick<LinearGradientConfig, "colors" | "start" | "end" | "locations">
> = {
  colors: ["transparent", "transparent"],
  start: { x: 0, y: 0 },
  end: { x: 0, y: 1 },
  locations: [0, 1],
};

export const isCssLinearGradientString = (value: unknown): value is string =>
  typeof value === "string" &&
  value.trim().toLowerCase().startsWith("linear-gradient(");

/**
 * Parses a limited CSS syntax:
 * - "linear-gradient(180deg, #FFFA98 0%, #CBA745 100%)"
 * Returns null if format is not recognized.
 */
export const parseCssLinearGradient = (
  cssString: string
): LinearGradientConfig | null => {
  try {
    const match = cssString.match(/linear-gradient\((\d+)deg,\s*([^)]+)\)/i);
    if (!match) return null;

    const angle = Number.parseInt(match[1], 10);
    const rawStops = match[2].split(",").map((s) => s.trim());

    const stops = rawStops
      .map((stop) => {
        const [color, loc] = stop.split(/\s+/);
        if (!color) return null;
        const location =
          loc && loc.endsWith("%")
            ? Number.parseFloat(loc.replace("%", "")) / 100
            : undefined;
        return { color, location };
      })
      .filter(Boolean) as Array<{ color: string; location?: number }>;

    if (stops.length === 0) return null;

    // Handle the common angles explicitly (good enough for now)
    let start: LinearGradientPoint = { x: 0, y: 0 };
    let end: LinearGradientPoint = { x: 0, y: 1 };
    if (angle === 0) {
      start = { x: 0, y: 0 };
      end = { x: 1, y: 0 };
    } else if (angle === 90) {
      start = { x: 0, y: 1 };
      end = { x: 0, y: 0 };
    } else if (angle === 180) {
      start = { x: 0, y: 0 };
      end = { x: 0, y: 1 };
    } else if (angle === 270) {
      start = { x: 1, y: 0 };
      end = { x: 0, y: 0 };
    }

    const colors = stops.map((s) => s.color);
    const locations = stops
      .map((s) => s.location)
      .filter((v): v is number => typeof v === "number");

    return {
      colors,
      start,
      end,
      locations: locations.length === colors.length ? locations : undefined,
    };
  } catch {
    return null;
  }
};

export const resolveToLinearGradientConfig = (
  value: unknown,
  fallbackColor: string
): Required<
  Pick<LinearGradientConfig, "colors" | "start" | "end" | "locations">
> => {
  if (isCssLinearGradientString(value)) {
    const parsed = parseCssLinearGradient(value);
    if (parsed?.colors?.length) {
      return {
        colors: parsed.colors,
        start: parsed.start ?? DEFAULT_GRADIENT.start,
        end: parsed.end ?? DEFAULT_GRADIENT.end,
        locations: parsed.locations ?? DEFAULT_GRADIENT.locations,
      };
    }
  }

  // Solid color (or unknown): render as a "flat" gradient.
  const color =
    typeof value === "string" && value.trim().length ? value : fallbackColor;
  return {
    colors: [color, color],
    start: DEFAULT_GRADIENT.start,
    end: DEFAULT_GRADIENT.end,
    locations: DEFAULT_GRADIENT.locations,
  };
};
